import { useEffect, useRef, useState } from 'react';

import { Turnstile } from '../components/turnstile.js';
import { formatPrice } from '../lib/format.js';
import { checkSlug, createSignup, type PublicPlan } from '../lib/signup-api.js';
import { EMPTY_DRAFT, type SignupDraft } from '../lib/signup-draft.js';
import { tryAutoSlug } from '../lib/slugify.js';

interface SignupFormProps {
  planId: string;
  /** The chosen plan, summarised (name, amount, cadence) above the submit button. */
  plan?: PublicPlan | null;
  initialDraft?: SignupDraft;
  onDraftChange?: (draft: SignupDraft) => void;
  /** Whether the chosen plan is entitled to a branded subdomain. */
  brandedSubdomain: boolean;
  onCreated: (result: {
    signupId: string;
    clientSecret: string;
    intentType: 'setup' | 'payment';
  }) => void;
}

// Keep in sync with packages/shared/src/subdomains.ts without adding a workspace
// dependency to signup-web's Docker build (see lib/slugify.ts).
const SUBDOMAIN_LABEL_RE = /^[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?$/;

type SlugStatus = 'idle' | 'checking' | 'available' | 'taken' | 'invalid' | 'reserved';

export function SignupForm({
  planId,
  plan,
  brandedSubdomain,
  onCreated,
  initialDraft = EMPTY_DRAFT,
  onDraftChange,
}: SignupFormProps) {
  const [communityName, setCommunityName] = useState(initialDraft.communityName);
  const [adminName, setAdminName] = useState(initialDraft.adminName);
  const [adminEmail, setAdminEmail] = useState(initialDraft.adminEmail);
  const [turnstileToken, setTurnstileToken] = useState('');
  const [pending, setPending] = useState(false);
  const submitting = useRef(false);
  const [error, setError] = useState<string | null>(null);

  const [slugEdited, setSlugEdited] = useState(initialDraft.slugEdited);
  const [slugInput, setSlugInput] = useState(initialDraft.slugInput);
  const [editingSlug, setEditingSlug] = useState(false);
  const [slugStatus, setSlugStatus] = useState<SlugStatus>('idle');

  useEffect(() => {
    onDraftChange?.({ communityName, adminName, adminEmail, slugEdited, slugInput });
  }, [communityName, adminName, adminEmail, slugEdited, slugInput, onDraftChange]);

  const autoSlug = tryAutoSlug(communityName);
  const effectiveSlug = slugEdited ? slugInput : autoSlug;
  // Auto-generation cannot produce a valid address (e.g. a non-Latin community
  // name) and the user has not typed one yet: ask for it explicitly.
  const needsExplicitSlug = brandedSubdomain && communityName.trim() !== '' && effectiveSlug === '';
  const showSlugInput = editingSlug || needsExplicitSlug;

  // Debounced advisory availability check. Only meaningful when the plan is
  // entitled to a branded subdomain; failures fall back to 'idle' (the API
  // re-validates on submit and 409s on a race collision).
  useEffect(() => {
    if (!brandedSubdomain) {
      setSlugStatus('idle');
      return;
    }
    if (!effectiveSlug || effectiveSlug.endsWith('-')) {
      setSlugStatus('idle');
      return;
    }
    setSlugStatus('checking');
    const t = setTimeout(async () => {
      try {
        const result = await checkSlug(effectiveSlug);
        if (result.available) {
          setSlugStatus('available');
        } else if (result.reason === 'invalid') {
          setSlugStatus(SUBDOMAIN_LABEL_RE.test(effectiveSlug) ? 'reserved' : 'invalid');
        } else {
          setSlugStatus('taken');
        }
      } catch {
        setSlugStatus('idle'); // availability is advisory; the API re-validates on submit
      }
    }, 400);
    return () => clearTimeout(t);
  }, [effectiveSlug, brandedSubdomain]);

  const slugBlocksSubmit =
    brandedSubdomain &&
    (slugStatus === 'taken' || slugStatus === 'invalid' || slugStatus === 'reserved');

  const valid =
    communityName.trim() !== '' &&
    adminName.trim() !== '' &&
    adminEmail.trim() !== '' &&
    turnstileToken !== '' &&
    !slugBlocksSubmit &&
    !needsExplicitSlug;

  async function handleSubmit(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!valid || pending || submitting.current) return;
    submitting.current = true;
    setPending(true);
    setError(null);
    try {
      const result = await createSignup({
        communityName: communityName.trim(),
        adminName: adminName.trim(),
        adminEmail: adminEmail.trim(),
        planId,
        turnstileToken,
        // Send the desired slug only when the plan is entitled and the slug is
        // not known-bad. Unentitled plans never send a slug (upsell nudge).
        ...(brandedSubdomain && effectiveSlug ? { slug: effectiveSlug } : {}),
      });
      onCreated(result);
    } catch (err) {
      const message =
        err instanceof Error ? err.message : 'Something went wrong. Please try again.';
      // A race collision surfaces as a 409 from the signup API; make it actionable.
      if (/no longer available|already/i.test(message) && brandedSubdomain) {
        setError('That address was just taken — pick another.');
        setSlugStatus('taken');
        setSlugEdited(true);
        setSlugInput(effectiveSlug);
        setEditingSlug(true);
      } else {
        setError(message);
      }
    } finally {
      submitting.current = false;
      setPending(false);
    }
  }

  return (
    <form onSubmit={handleSubmit} className="flex flex-col gap-5">
      <div className="flex flex-col gap-1.5">
        <label htmlFor="communityName" className="text-sm font-medium text-gray-700">
          Community name
        </label>
        <input
          id="communityName"
          type="text"
          value={communityName}
          onChange={(e) => setCommunityName(e.target.value)}
          className="rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-brand focus:outline-none focus:ring-1 focus:ring-brand"
        />

        {brandedSubdomain ? (
          <div className="mt-1.5 flex flex-col gap-1.5">
            {showSlugInput ? (
              <div className="flex items-center rounded-md border border-gray-300 px-3 py-2 text-sm focus-within:border-brand focus-within:ring-1 focus-within:ring-brand">
                <input
                  id="slug"
                  type="text"
                  aria-label="Subdomain"
                  aria-describedby="slug-format-hint slug-hint"
                  autoFocus={editingSlug}
                  value={slugInput}
                  onChange={(e) => {
                    setSlugEdited(true);
                    setEditingSlug(true);
                    // Light live transform: preserve a single trailing hyphen while
                    // typing so 'foo-bar' can be typed char-by-char without the
                    // hyphen being swallowed. Full slugify() is applied on blur
                    // (and on submit via effectiveSlug).
                    const v = e.target.value
                      .toLowerCase()
                      .replace(/[^a-z0-9-]+/g, '-')
                      .replace(/-{2,}/g, '-')
                      .slice(0, 48);
                    setSlugInput(/^-*$/.test(v) ? '' : v);
                  }}
                  onBlur={() => {
                    // On blur apply full slugify to normalise any trailing hyphen.
                    const cleaned = tryAutoSlug(slugInput);
                    setSlugInput(cleaned);
                    setEditingSlug(false);
                  }}
                  className="min-w-0 flex-1 bg-transparent focus:outline-none"
                />
                <span className="pl-1 text-gray-400">.groupfire.com</span>
              </div>
            ) : (
              <p className="text-sm text-gray-500">
                Your community will live at{' '}
                <span className={effectiveSlug ? 'font-medium text-gray-800' : 'text-gray-400'}>
                  {effectiveSlug || 'your-address'}.groupfire.com
                </span>{' '}
                {effectiveSlug && (
                  <button
                    type="button"
                    onClick={() => {
                      setSlugEdited(true);
                      setSlugInput(effectiveSlug);
                      setEditingSlug(true);
                    }}
                    className="font-medium text-brand hover:underline"
                  >
                    edit
                  </button>
                )}
              </p>
            )}

            {showSlugInput && (
              <p id="slug-format-hint" className="text-xs text-gray-500">
                {needsExplicitSlug && (
                  <>We can’t build a web address from “{communityName.trim()}”. </>
                )}
                Enter an address using lowercase English letters (a–z), numbers and hyphens, like{' '}
                <span className="font-medium">tokyo-friends</span>. Your community name can stay as
                you typed it.
              </p>
            )}

            <div id="slug-hint" aria-live="polite">
              {slugStatus === 'checking' && (
                <p className="text-xs text-gray-400">Checking availability…</p>
              )}
              {slugStatus === 'available' && <p className="text-xs text-green-600">✓ Available</p>}
              {slugStatus === 'taken' && (
                <p className="text-xs text-red-600">Already taken — try another.</p>
              )}
              {slugStatus === 'invalid' && (
                <p className="text-xs text-red-600">
                  Use only lowercase letters (a–z), numbers and hyphens, with no hyphen at the start
                  or end — for example tokyo-friends.
                </p>
              )}
              {slugStatus === 'reserved' && (
                <p className="text-xs text-red-600">
                  That address is reserved — choose another, for example tokyo-friends.
                </p>
              )}
            </div>
          </div>
        ) : (
          <p className="mt-1.5 text-sm text-gray-400">
            Branded web address — available on higher plans
          </p>
        )}
      </div>

      <div className="flex flex-col gap-1.5">
        <label htmlFor="adminName" className="text-sm font-medium text-gray-700">
          Your name
        </label>
        <input
          id="adminName"
          type="text"
          value={adminName}
          onChange={(e) => setAdminName(e.target.value)}
          className="rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-brand focus:outline-none focus:ring-1 focus:ring-brand"
        />
      </div>

      <div className="flex flex-col gap-1.5">
        <label htmlFor="adminEmail" className="text-sm font-medium text-gray-700">
          Email address
        </label>
        <input
          id="adminEmail"
          type="email"
          value={adminEmail}
          onChange={(e) => setAdminEmail(e.target.value)}
          className="rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-brand focus:outline-none focus:ring-1 focus:ring-brand"
        />
      </div>

      <Turnstile onToken={setTurnstileToken} />

      {error && (
        <p role="alert" className="text-sm text-red-600">
          {error}
        </p>
      )}

      {plan && (
        <section
          aria-label="Your plan"
          className="flex flex-wrap items-baseline justify-between gap-x-3 gap-y-1 rounded-md border border-gray-200 bg-gray-50 px-3 py-2.5"
        >
          <p className="min-w-0 break-words text-sm font-medium text-gray-900">{plan.name}</p>
          <p className="text-sm font-semibold text-gray-900">
            {formatPrice(plan.unitAmount, plan.currency)}
            <span className="font-normal text-gray-500">/{plan.interval}</span>
          </p>
        </section>
      )}

      <button
        type="submit"
        disabled={!valid || pending}
        className="w-full rounded-md bg-brand px-4 py-2.5 text-sm font-semibold text-white hover:bg-brand-dark disabled:cursor-not-allowed disabled:opacity-50 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand"
      >
        {pending ? 'Creating…' : 'Continue to payment'}
      </button>
    </form>
  );
}
